import { AnchorButton, EmptyState, SkeletonLoader } from '@payaca/ambrosia';
import { FC, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';

import AddEditProposalLineItemContent, {
  AddEditProposalLineItemContentProps,
} from '@/ui/components/addEditProposalLineItem/AddEditProposalLineItemContent';
import useProposalLineItemEditor from '@/ui/components/addEditProposalLineItem/useProposalLineItemEditor';
import SidebarSplit from '@/ui/components/sidebarSplit/SidebarSplit';

const AUTOSAVE_FIELD_DEBOUNCE_MS = {
  description: 1000,
} satisfies AddEditProposalLineItemContentProps['autosaveFieldDebounceMs'];

export type TProps = {
  proposalId: string;
  proposalLineItemId: string;
  /** Route to return to when the editor is closed or the item is removed. */
  backTo: string;
};

/**
 * Inline Proposal Line Item editor for the Proposal Builder. Wraps the shared
 * proposal line item form in autosave mode — there is no Sidebar to close, so
 * edits persist as the user types and leaving is a route change back to the
 * owning section tab.
 */
const ProposalItemEditor: FC<TProps> = (props) => {
  const { proposalId, proposalLineItemId, backTo } = props;

  const history = useHistory();
  const goBack = () => history.push(backTo);

  const [isLinkMaterialSidebarOpen, setIsLinkMaterialSidebarOpen] =
    useState(false);

  const {
    proposalLineItemData,
    isLoading,
    isNotFound,
    hasError,
    isSavingThumbnail,
    saveMutationKey,
    onSubmit,
    onThumbnailDelete,
    onDelete,
  } = useProposalLineItemEditor({
    proposalLineItemId,
    proposalId,
    proposalCacheStrategy: 'hydrate',
    onRemoveSuccess: goBack,
  });

  const isUnavailable = isNotFound || hasError;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-none items-center gap-2 border-b p-3.5">
        <AnchorButton as={Link} to={backTo} variant="link">
          Back to items
        </AnchorButton>
      </div>
      <div className="min-h-0 flex-1">
        {isNotFound && (
          <ItemUnavailable text="This item has been removed from the proposal" />
        )}
        {hasError && <ItemUnavailable text="This item could not be loaded." />}
        {isLoading && <ItemEditorSkeleton />}
        {!isUnavailable && !isLoading && (
          <AddEditProposalLineItemContent
            saveMode="autosave"
            autosaveFieldDebounceMs={AUTOSAVE_FIELD_DEBOUNCE_MS}
            isSaving={isSavingThumbnail}
            savingMutationKey={saveMutationKey}
            proposalLineItemId={proposalLineItemId}
            proposalLineItemData={proposalLineItemData}
            isLinkMaterialSidebarOpen={isLinkMaterialSidebarOpen}
            setIsLinkMaterialSidebarOpen={setIsLinkMaterialSidebarOpen}
            onSubmit={onSubmit}
            onThumbnailDelete={onThumbnailDelete}
            onDelete={onDelete}
            backTo={backTo}
          />
        )}
      </div>
    </div>
  );
};

/** Shown in place of the editor when the Line Item cannot be edited. */
const ItemUnavailable: FC<{ text: string }> = (props) => {
  const { text } = props;

  return (
    <div role="status">
      <EmptyState className="py-8" iconName="alert-triangle.3" text={text} />
    </div>
  );
};

const ItemEditorSkeleton: FC = () => (
  <SidebarSplit
    leftTab={{ label: 'Item details' }}
    rightTab={{ label: 'Linked materials' }}
  >
    <div className="flex flex-col gap-4 p-3.5">
      <SkeletonLoader.Input />
      <SkeletonLoader.Textarea />
      <SkeletonLoader.Input />
      <SkeletonLoader.Input />

      <div className="mt-auto">
        <SkeletonLoader.Button />
      </div>
    </div>
    <div className="space-y-4 @screen-md/sidebarsplit:bg-gray-50 p-3.5">
      <SkeletonLoader.MaterialCard />
      <SkeletonLoader.MaterialCard />
      <SkeletonLoader.MaterialCard />
      <SkeletonLoader.MaterialCard />
      <SkeletonLoader.MaterialCard />
      <SkeletonLoader.MaterialCard />
      <SkeletonLoader.MaterialCard />
    </div>
  </SidebarSplit>
);

export default ProposalItemEditor;
